import { formatRevisionLimit } from '../../utils/projectStatus';
import { pluralize } from '../../utils/format';
import { GENERIC_RATE_LIMIT_MESSAGE, isRateLimitMessage } from '../../utils/rateLimitMessage';

/**
 * Pure rules for the creator's review page: which cut is waiting for a
 * decision, what the header says happens next, and how errors read.
 */

/** Player selection for the source footage (revisions use their id). */
export const ORIGINAL_KEY = 'original';

/** The one review decision, made per revision. */
export type DecisionType = 'approve' | 'changes';

const upper = (value?: string | null) => String(value || '').trim().toUpperCase();

/** Only READY revisions can be approved or sent back (the backend refuses the rest). */
export const isReadyRevision = (status?: string | null) => upper(status) === 'READY';

/** The creator approved this cut. */
export const isAcceptedRevision = (status?: string | null) => upper(status) === 'ACCEPTED';

/** A decision was made on this cut: approved, or changes requested. */
const isDecidedRevision = (status?: string | null) => ['ACCEPTED', 'REVISION_REQUESTED'].includes(upper(status));

interface VersionLike {
  version: number;
  revision: { status?: string | null };
}

/**
 * The approved cut of an approved or delivered project, and the versions
 * uploaded after it that nobody has reviewed. Editors can still upload once a
 * cut is approved (final files, say), and such an upload must not take the
 * approved cut's place. `versions` is newest first, as buildRevisionVersions
 * returns it.
 */
export const approvedCutOf = <V extends VersionLike>(
  versions: V[],
  projectStatus?: string | null
): { approved: V | undefined; unreviewedAfter: number[] } => {
  const key = upper(projectStatus);
  const settled = key === 'FINAL_APPROVAL' || key === 'DELIVERED' || key === 'COMPLETED';
  const approved = settled ? versions.find((entry) => isAcceptedRevision(entry.revision.status)) : undefined;
  const unreviewedAfter = approved
    ? versions
        .filter((entry) => entry.version > approved.version && !isDecidedRevision(entry.revision.status))
        .map((entry) => entry.version)
        .sort((a, b) => a - b)
    : [];
  return { approved, unreviewedAfter };
};

/** "v2", "v2 and v3", "v2, v3 and v4". */
const listVersions = (versions: number[]): string => {
  const labels = versions.map((version) => `v${version}`);
  return labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}` : labels[0] || '';
};

export const isMulticamSource = (sourceType?: string | null) => upper(sourceType) === 'MULTICAM';

/**
 * "1 of 2 change rounds used". Plans with no cap (Infinity) read
 * "1 change round used · no limit".
 */
export const describeChangeRounds = (used: number | null | undefined, limit: number): string => {
  const count = Math.max(0, Math.floor(Number(used) || 0));
  if (!Number.isFinite(limit)) return `${pluralize(count, 'change round')} used · no limit`;
  return `${count} of ${formatRevisionLimit(limit)} change round${limit === 1 ? '' : 's'} used`;
};

/** "8 notes · 1 image · 1 audio file"; empty when there is nothing attached. */
export const describeAttachments = (notes: number, images: number, audio: number): string =>
  [
    notes > 0 ? pluralize(notes, 'note') : null,
    images > 0 ? pluralize(images, 'image') : null,
    audio > 0 ? pluralize(audio, 'audio file') : null,
  ]
    .filter(Boolean)
    .join(' · ');

interface GuidanceInput {
  status?: string | null;
  /** Version number of the newest READY revision, if any. */
  waitingVersion?: number | null;
  /** Number of uploaded versions. */
  versionCount: number;
  /** Version number of the approved cut, once the project is approved. */
  approvedVersion?: number | null;
  /** Versions uploaded after the approved cut that nobody has reviewed. */
  unreviewedAfter?: number[];
  editorName?: string | null;
  isOwner: boolean;
}

/** One sentence under the title on what happens next, for the creator. */
export const creatorGuidance = ({
  status,
  waitingVersion,
  versionCount,
  approvedVersion,
  unreviewedAfter = [],
  editorName,
  isOwner,
}: GuidanceInput): string => {
  const key = upper(status);
  const editor = editorName?.trim() || 'Your editor';
  const latest = versionCount > 0 ? `v${versionCount}` : null;
  const next = versionCount > 0 ? `v${versionCount + 1}` : 'the first cut';

  if (key === 'CANCELLED') return 'This project was cancelled. It stays here for your records.';
  if (key === 'DELIVERED' || key === 'COMPLETED') return 'Delivered. This project is complete.';
  if (waitingVersion) {
    return `v${waitingVersion} is ready for your review. Watch it, then approve it or request changes.`;
  }

  switch (key) {
    case 'FINAL_APPROVAL': {
      // The approved cut, not a later upload nobody has reviewed.
      const approved = approvedVersion ? `v${approvedVersion}` : latest;
      const later = unreviewedAfter.length
        ? unreviewedAfter.length === 1
          ? ` ${listVersions(unreviewedAfter)} was uploaded later and hasn't been reviewed.`
          : ` ${listVersions(unreviewedAfter)} were uploaded later and haven't been reviewed.`
        : '';
      return isOwner
        ? `You approved ${approved ?? 'the cut'}.${later} Close the project when you have everything you need.`
        : `${approved ?? 'The cut'} was approved.${later}`;
    }
    case 'REVISION_REQUESTED':
      return `You requested changes${latest ? ` on ${latest}` : ''}. ${editor} will upload ${next}.`;
    case 'VIDEO_SUBMITTED':
    case 'EDITOR_ASSIGNED':
    case 'NEW':
    case 'PENDING':
    case 'ASSIGNED':
      return editorName?.trim()
        ? `Waiting for ${editorName.trim()} to accept the project.`
        : 'No editor is assigned yet. Assign one from your projects list.';
    case 'REQUIREMENTS_REVIEW':
    case 'EDITING_IN_PROGRESS':
    case 'IN PROGRESS':
      return `${editor} is working on it. You'll be notified when ${next} is ready.`;
    case 'DRAFT_READY':
      return `${editor} has a draft ready and will send it to you for review.`;
    case 'CLIENT_REVIEW':
      // In review, but no cut waiting (older projects kept the cut elsewhere).
      return `${editor} sent this project for review, but there's no cut here to review yet. Send them a message if you expected one.`;
    default:
      return `${editor} is working on this project.`;
  }
};

// Wording that only makes sense to a developer: network failures, masked
// server errors and crash text. Never shown; the caller's fallback is.
const TECHNICAL_ERROR = /failed to fetch|networkerror|network request failed|load failed|response not successful|status code|unexpected error|internal server error|typeerror|referenceerror|cannot read|undefined|null|graphql|json|exception/i;

/**
 * Text for an error from a review action. The backend's own sentences pass
 * through (they are written for customers); the change-round limit and stale
 * reviews are reworded in the page's terms; anything technical becomes
 * `fallback`.
 */
export const readableError = (error: unknown, fallback: string): string => {
  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const message = String(raw || '').split('\n')[0].trim();
  if (!message) return fallback;

  const limit = message.match(/maximum number of revisions \((\d+)\)/i);
  if (limit) {
    const n = Number(limit[1]);
    return `This project has no change rounds left (${n} of ${n} used). Approve this version, or send your editor a message.`;
  }
  if (/only ready revisions can be reviewed|invalid status transition/i.test(message)) {
    return 'This version can’t be reviewed right now. Refresh the page to see its latest status.';
  }
  if (isRateLimitMessage(message)) return GENERIC_RATE_LIMIT_MESSAGE;
  if (TECHNICAL_ERROR.test(message)) return fallback;
  return message;
};

/** True for the rejection we raise when a load is cancelled (nothing to report). */
export const isAbortError = (error: unknown): boolean =>
  Boolean(error && typeof error === 'object' && (error as { name?: string }).name === 'AbortError');
